import { useState } from "react";
import { useNavigate } from "react-router-dom";
import IntranetLayout from "@/components/IntranetLayout";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

export default function EarMarNew() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [type, setType] = useState<"EAR" | "MAR" | "MOC">("EAR");
  const [title, setTitle] = useState("");
  const [vesselName, setVesselName] = useState("");
  const [description, setDescription] = useState("");
  const [costStr, setCostStr] = useState("0");
  const [submitting, setSubmitting] = useState(false);

  const cost = Number(costStr) || 0;

  const onCreate = async () => {
    if (!user) return;
    if (!title.trim()) { toast.error("Title is required"); return; }
    setSubmitting(true);
    const { data, error } = await supabase.from("ear_mar_projects").insert({
      type, title: title.trim(), vessel_name: vesselName.trim() || null,
      description: description.trim() || null, estimated_cost_usd: cost,
      created_by: user.id,
    }).select("id").single();
    setSubmitting(false);
    if (error || !data) { toast.error(error?.message || "Failed to create"); return; }
    await supabase.from("ear_mar_assignees").insert({ project_id: data.id, user_id: user.id, role: "owner" });
    toast.success("Project created");
    navigate(`/employee/ear-mar/${data.id}`);
  };

  return (
    <IntranetLayout>
      <div className="mb-6">
        <p className="text-[11px] font-bold tracking-[3px] uppercase text-teal/70 mb-1">Engineering & Marine</p>
        <h1 className="text-3xl font-bold">New {type} Project</h1>
      </div>

      <Card className="max-w-2xl">
        <CardContent className="p-6 space-y-5">
          <div className="space-y-2">
            <Label>Type</Label>
            <Select value={type} onValueChange={(v) => setType(v as any)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="EAR">EAR — Engineering Assistance Request</SelectItem>
                <SelectItem value="MAR">MAR — Marine Assistance Request</SelectItem>
                <SelectItem value="MOC">MOC — Management of Change</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Title *</Label>
            <Input value={title} onChange={e => setTitle(e.target.value)} placeholder="E.g. Hydraulic crane drawing revision — Vanguard 02" />
          </div>

          <div className="space-y-2">
            <Label>Vessel</Label>
            <Input value={vesselName} onChange={e => setVesselName(e.target.value)} placeholder="Vessel name" />
          </div>

          <div className="space-y-2">
            <Label>Description</Label>
            <Textarea rows={5} value={description} onChange={e => setDescription(e.target.value)} placeholder="Scope, background, why this is needed…" />
          </div>

          <div className="space-y-2">
            <Label>Estimated cost (USD)</Label>
            <Input type="number" value={costStr} onChange={e => setCostStr(e.target.value)} onBlur={() => setCostStr(String(Number(costStr) || 0))} />
            {cost > 50000 && <p className="text-xs text-purple-300">&gt; $50,000 — approval chain will route through the COO.</p>}
          </div>

          <div className="flex gap-2 justify-end pt-2">
            <Button variant="outline" onClick={() => navigate("/employee/ear-mar")}>Cancel</Button>
            <Button onClick={onCreate} disabled={submitting} className="bg-teal hover:bg-teal/90">
              {submitting ? "Creating…" : "Create project"}
            </Button>
          </div>
        </CardContent>
      </Card>
    </IntranetLayout>
  );
}
